import { useState, useEffect, useRef } from 'react';

import {
  getLastMonthRange,
  getLastQuarterRange,
  getLastYearRange,
  getThisMonthRange,
  getThisQuarterRange,
  getThisYearRange,
} from '@/shared/utils/date-utils';

export type PredefinedPeriod =
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'thisYear'
  | 'lastYear';

export type SelectionMode = 'today' | 'predefined' | 'custom';

type UseDateRangePickerParams = {
  startDate?: Date;
  endDate?: Date;
  onDateRangeSelect: (
    startDate: Date | undefined,
    endDate: Date | undefined,
  ) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  showPredefinedPeriods?: boolean;
};

function getRangeForPeriod(period: PredefinedPeriod): {
  start: Date;
  end: Date;
} {
  switch (period) {
    case 'thisMonth':
      return getThisMonthRange();
    case 'lastMonth':
      return getLastMonthRange();
    case 'thisQuarter':
      return getThisQuarterRange();
    case 'lastQuarter':
      return getLastQuarterRange();
    case 'thisYear':
      return getThisYearRange();
    case 'lastYear':
      return getLastYearRange();
  }
}

function getTodayRange(): { start: Date; end: Date } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  start.setHours(0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export function useDateRangePicker({
  startDate,
  endDate,
  onDateRangeSelect,
  isOpen,
  onOpenChange,
  showPredefinedPeriods = false,
}: UseDateRangePickerParams) {
  // Ensure dates are Date objects
  const normalizedStartDate =
    startDate instanceof Date
      ? startDate
      : startDate
        ? new Date(startDate)
        : undefined;
  const normalizedEndDate =
    endDate instanceof Date ? endDate : endDate ? new Date(endDate) : undefined;

  const [tempStartDate, setTempStartDate] = useState<Date | undefined>(
    normalizedStartDate,
  );
  const [tempEndDate, setTempEndDate] = useState<Date | undefined>(
    normalizedEndDate,
  );

  // Selection mode: today, predefined (dropdown), or custom (calendar)
  const [selectionMode, setSelectionMode] = useState<SelectionMode>('custom');

  // Selected period within the dropdown
  const [selectedPeriod, setSelectedPeriod] =
    useState<PredefinedPeriod>('thisQuarter');

  // Sync temp dates when external startDate/endDate change
  useEffect(() => {
    setTempStartDate(normalizedStartDate);
    setTempEndDate(normalizedEndDate);
  }, [normalizedStartDate, normalizedEndDate]);

  // Detect selection mode when dialog opens
  const prevIsOpen = useRef(isOpen);
  useEffect(() => {
    if (!showPredefinedPeriods) return;

    // Only detect when dialog first opens
    if (isOpen && !prevIsOpen.current) {
      if (normalizedStartDate && normalizedEndDate) {
        // Check if it's today
        const today = getTodayRange();
        if (
          normalizedStartDate.getTime() === today.start.getTime() &&
          normalizedEndDate.getTime() === today.end.getTime()
        ) {
          setSelectionMode('today');
          return;
        }

        // Check predefined periods
        const periods: PredefinedPeriod[] = [
          'thisMonth',
          'lastMonth',
          'thisQuarter',
          'lastQuarter',
          'thisYear',
          'lastYear',
        ];

        for (const period of periods) {
          const range = getRangeForPeriod(period);
          if (
            normalizedStartDate.getTime() === range.start.getTime() &&
            normalizedEndDate.getTime() === range.end.getTime()
          ) {
            setSelectionMode('predefined');
            setSelectedPeriod(period);
            return;
          }
        }

        // Otherwise it's custom
        setSelectionMode('custom');
      } else {
        setSelectionMode('custom');
      }
    }

    prevIsOpen.current = isOpen;
  }, [isOpen, normalizedStartDate, normalizedEndDate, showPredefinedPeriods]);

  // Handle selection mode change (radio button)
  const handleSelectionModeChange = (mode: SelectionMode) => {
    setSelectionMode(mode);

    if (mode === 'today') {
      const today = getTodayRange();
      setTempStartDate(today.start);
      setTempEndDate(today.end);
    } else if (mode === 'predefined') {
      const range = getRangeForPeriod(selectedPeriod);
      setTempStartDate(range.start);
      setTempEndDate(range.end);
    }
    // For custom, keep the current dates or let user select in calendar
  };

  // Handle period selection from dropdown
  const handlePeriodSelect = (period: PredefinedPeriod) => {
    setSelectedPeriod(period);
    // Also set selection mode to predefined when selecting from dropdown
    setSelectionMode('predefined');

    const range = getRangeForPeriod(period);
    setTempStartDate(range.start);
    setTempEndDate(range.end);
  };

  const handleReset = () => {
    setTempStartDate(undefined);
    setTempEndDate(undefined);
    if (showPredefinedPeriods) {
      setSelectionMode('custom');
    }
  };

  const handleApply = () => {
    onDateRangeSelect(tempStartDate, tempEndDate);
    onOpenChange(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset temp dates when closing without applying
      setTempStartDate(normalizedStartDate);
      setTempEndDate(normalizedEndDate);
    }
    onOpenChange(open);
  };

  return {
    tempStartDate,
    tempEndDate,
    setTempStartDate,
    setTempEndDate,
    handleReset,
    handleApply,
    handleOpenChange,
    selectionMode,
    handleSelectionModeChange,
    selectedPeriod,
    handlePeriodSelect,
  };
}
